import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Card, CardContent, Button, Stack, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Alert, IconButton, Tooltip,
  LinearProgress, Divider, Grid, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { useNavigate } from 'react-router-dom';
import ChooseServicesDialog from '../components/ChooseServicesDialog';
import VendorReadinessPanel from '../components/VendorReadinessPanel';
import VendorServiceCsvDialog from '../components/VendorServiceCsvDialog';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  packageLabelsFor, OfferingPackagesEditor, OfferingPackagesView, packagesToInput, packageProblems,
} from '../components/OfferingPackages';
import {
  VENDOR_OFFERING_FIELD_DEFS_QUERY, MY_VENDOR_SERVICE_CATEGORIES_QUERY, MY_VENDOR_OFFERINGS_QUERY,
  CREATE_VENDOR_OFFERING_MUTATION, UPDATE_VENDOR_OFFERING_MUTATION, DELETE_VENDOR_OFFERING_MUTATION,
  WITHDRAW_VENDOR_SERVICE_CONTRACT_MUTATION, MY_VENDOR_READINESS_QUERY, ALL_SERVICE_CATEGORIES_QUERY,
} from '../graphql/queries';
import {
  fieldsForCategory, OfferingDetailInputs, OfferingDetailList, missingRequiredDetails, formatPriceRange,
} from '../components/OfferingDetails';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_LABEL = {
  sent_for_review: 'Contract awaiting your review',
  user_requested_changes: 'Changes requested',
  user_agreed: 'Awaiting signatures',
  signed: 'Contract signed',
};

// What still has to happen before a contracted service goes live
function pendingStep(cat) {
  if (cat.isAccepted) return null;
  if (cat.isSigned) return 'Signed and approved by the admin. Accept the contract under Contracts to make your offerings live.';
  if (cat.contractStatus === 'sent_for_review') return 'Review and agree to the contract under Contracts.';
  if (cat.contractStatus === 'user_requested_changes') return 'You requested changes; the admin will update the contract.';
  if (!cat.vendorSigned) return 'Sign the contract under Contracts; the admin approves it after you sign.';
  return 'You have signed. Waiting for the admin to approve and countersign.';
}

const EMPTY_FORM = {
  category: '', title: '', description: '', businessName: '', city: '', serviceArea: '',
  priceFrom: '', priceTo: '', priceUnit: 'per event', contactName: '', contactPhone: '', contactEmail: '',
  cancellationPolicy: '', details: {}, packages: [], isActive: true,
};

// Vendor's own services: only the categories in their contract with the admin.
// Vendors use this page for their own services. An admin can open it for any vendor
// (Admin > Vendors > Manage services): every request then acts for that vendor.
export default function VendorServicesPage({ vendorUserId, vendorName }) {
  const forAdmin = Boolean(vendorUserId);
  const context = forAdmin ? { headers: { 'X-Vendor-User-Id': vendorUserId } } : undefined;
  const fetchPolicy = forAdmin ? 'network-only' : 'cache-and-network';
  const contractsPath = forAdmin ? '/dashboard/admin/contracts' : '/dashboard/contracts';
  const { data: defsData } = useQuery(VENDOR_OFFERING_FIELD_DEFS_QUERY);
  const { data: catData, loading: catLoading, refetch: refetchCats } = useQuery(MY_VENDOR_SERVICE_CATEGORIES_QUERY, { fetchPolicy, context });
  const { data, loading, refetch: refetchOfferings } = useQuery(MY_VENDOR_OFFERINGS_QUERY, { fetchPolicy, context });
  // What must be entered / uploaded before a contract (and every service on the platform, so
  // details can be prepared before the service is in a contract)
  const { data: readyData, loading: readyLoading, error: readyError, refetch: refetchReadiness } = useQuery(MY_VENDOR_READINESS_QUERY, { fetchPolicy: 'network-only', context });
  const { data: allCatData } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, { variables: { activeOnly: true } });
  const refetch = () => { refetchOfferings(); refetchReadiness(); };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(null); // a service (category) to withdraw
  const [chooseOpen, setChooseOpen] = useState(false);
  const [csvFor, setCsvFor] = useState(null); // null = closed, '' = no service chosen yet
  const [notice, setNotice] = useState(null);
  const navigate = useNavigate();

  const fieldDefs = defsData?.vendorOfferingFields;
  const categories = catData?.myVendorServiceCategories || [];
  const readiness = readyData?.myVendorReadiness;
  // Services are added once the admin approved the owner ID and business document (an admin
  // working for the vendor can add them before)
  const canAdd = forAdmin || Boolean(readiness?.documentsApproved);
  const lockedTip = 'Available once the admin approves your owner ID and business document';
  // While waiting for the approval, check again every 30 seconds and when the vendor comes back
  // to the tab, so the page unlocks without a reload
  const waiting = !forAdmin && Boolean(readiness) && !readiness.documentsApproved;
  useEffect(() => {
    if (!waiting) return undefined;
    const check = () => { if (document.visibilityState === 'visible') refetchReadiness(); };
    const timer = setInterval(check, 30000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => { clearInterval(timer); window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check); };
  }, [waiting, refetchReadiness]);
  const wasWaiting = useRef(false);
  useEffect(() => {
    if (waiting) wasWaiting.current = true;
    else if (wasWaiting.current && readiness?.documentsApproved) {
      wasWaiting.current = false;
      setNotice({ severity: 'success', text: 'Your documents were approved. You can now add your services: use Add service details or Upload services (CSV).' });
    }
  }, [waiting, readiness]);
  // Every platform service can be prepared; contracted ones first
  const serviceOptions = [
    ...categories.map(c => ({ category: c.category, displayName: c.displayName, contracted: true })),
    ...(allCatData?.allServiceCategories || []).filter(c => !categories.some(k => k.category === c.name))
      .map(c => ({ category: c.name, displayName: c.displayName, contracted: false })),
  ];
  const draftServices = (readiness?.services || []).filter(s => !s.contracted);
  const offerings = data?.myVendorOfferings || [];
  const detailFields = fieldsForCategory(fieldDefs, form.category);
  const packageLabels = packageLabelsFor(fieldDefs, form.category);

  const onSaved = () => { setDialogOpen(false); setEditing(null); setForm(EMPTY_FORM); setError(''); refetch(); };
  const [createOffering, { loading: creating }] = useMutation(CREATE_VENDOR_OFFERING_MUTATION, { context, onCompleted: onSaved, onError: e => setError(e.message) });
  const [updateOffering, { loading: updating }] = useMutation(UPDATE_VENDOR_OFFERING_MUTATION, { context, onCompleted: onSaved, onError: e => setError(e.message) });
  const [deleteOffering] = useMutation(DELETE_VENDOR_OFFERING_MUTATION, { context, onCompleted: () => { setConfirmDelete(null); refetch(); } });
  const [withdrawContract] = useMutation(WITHDRAW_VENDOR_SERVICE_CONTRACT_MUTATION, {
    context,
    onCompleted: () => {
      const w = confirmWithdraw;
      setNotice({ severity: 'info', text: w && w.others > 0
        ? `${w.displayName} withdrawn. The other services on ${w.contractRef || 'the contract'} are unchanged.`
        : 'Contract request withdrawn.' });
      setConfirmWithdraw(null); refetchCats(); refetch();
    },
    onError: e => setNotice({ severity: 'error', text: e.message }),
  });
  const onContractCreated = (contract) => {
    setChooseOpen(false);
    setNotice({
      severity: 'success',
      text: forAdmin
        ? `Contract ${contract.refNumber || ''} created and sent to the vendor to review and sign. Countersign it under Contracts once they have signed.`
        : `Contract ${contract.refNumber || ''} created. Sign it under Contracts; once the admin approves it, your offerings for these services go live.`,
      action: true,
    });
    refetchCats(); refetchReadiness();
  };

  const openCreate = (category = '') => {
    const last = offerings[0];
    // Reuse business/contact details from an existing offering to save retyping
    setForm({
      ...EMPTY_FORM, category,
      businessName: last?.businessName || '', city: last?.city || '',
      contactName: last?.contactName || '', contactPhone: last?.contactPhone || '', contactEmail: last?.contactEmail || '',
      cancellationPolicy: last?.cancellationPolicy || '',
    });
    setEditing(null); setError(''); setDialogOpen(true);
  };
  const openEdit = (o) => {
    setForm({
      category: o.category, title: o.title, description: o.description, businessName: o.businessName, city: o.city,
      serviceArea: o.serviceArea || '', priceFrom: o.priceFrom, priceTo: o.priceTo ?? '', priceUnit: o.priceUnit,
      contactName: o.contactName || '', contactPhone: o.contactPhone, contactEmail: o.contactEmail || '',
      cancellationPolicy: o.cancellationPolicy || '',
      details: o.details || {}, isActive: o.isActive,
      packages: (o.packages || []).map(pk => ({ ...pk, items: pk.items.map(it => ({ ...it })) })),
    });
    setEditing(o); setError(''); setDialogOpen(true);
  };

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSave = () => {
    if (missingBasics.length) { setError(`Please add ${missingBasics.join(', ')}.`); return; }
    const missing = missingRequiredDetails(detailFields, form.details);
    if (missing.length) { setError(`Please complete: ${missing.join(', ')}`); return; }
    const pkgProblems = packageProblems(form.packages, packageLabels);
    if (pkgProblems.length) { setError(pkgProblems.join('; ')); return; }
    const input = {
      ...form,
      packages: packagesToInput(form.packages),
      priceFrom: Number(form.priceFrom),
      priceTo: form.priceTo === '' || form.priceTo === null ? null : Number(form.priceTo),
    };
    if (editing) updateOffering({ variables: { id: editing.id, input } });
    else createOffering({ variables: { input } });
  };

  // What the form still needs (shown when Save is pressed, rather than a silently disabled button)
  const missingBasics = [
    !form.category && 'the service', !form.title.trim() && 'an offering title', !form.description.trim() && 'a description',
    !form.businessName.trim() && 'the business name', !form.city.trim() && 'the city', !form.contactPhone.trim() && 'a contact phone',
    form.priceFrom === '' && 'a starting price',
    form.cancellationPolicy.trim().length < 20 && 'cancellation & refund terms (at least a sentence)',
  ].filter(Boolean);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{forAdmin ? `${vendorName || 'Vendor'} — Services` : 'My Services'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {forAdmin
              ? 'Choose services for this vendor and manage their offerings. New services are sent to the vendor to review and sign; you then countersign under Contracts.'
              : 'Add the details of each service in your contract. Families and matchmakers see an offering once the contract for that service is signed.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<PlaylistAddCheckIcon />} onClick={() => setChooseOpen(true)}>
            Choose Services
          </Button>
          <Tooltip title={canAdd ? '' : lockedTip}>
            <span>
              <Button variant="outlined" startIcon={<UploadFileIcon />} disabled={!canAdd} onClick={() => setCsvFor('')}>
                Upload services (CSV)
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? '' : lockedTip}>
            <span>
              <Button variant="contained" startIcon={<AddIcon />} disabled={!canAdd} onClick={() => openCreate()}>
                Add service details
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {notice && (
        <Alert
          severity={notice.severity} sx={{ mb: 3 }} onClose={() => setNotice(null)}
          action={notice.action ? <Button color="inherit" size="small" onClick={() => navigate(contractsPath)}>Sign now</Button> : undefined}
        >
          {notice.text}
        </Alert>
      )}

      {(loading || catLoading) && <LinearProgress sx={{ mb: 2 }} />}

      {readyError && <Alert severity="error" sx={{ mb: 2 }}>The checklist and document upload could not be loaded: {readyError.message}</Alert>}
      <VendorReadinessPanel
        readiness={readiness} loading={readyLoading} vendorUserId={vendorUserId} onRefresh={refetchReadiness} canAdd={canAdd}
        onAddDetails={(category) => openCreate(category)}
        onEdit={(id) => { const o = offerings.find(x => x.id === id); if (o) openEdit(o); }}
        onRequestContract={() => setChooseOpen(true)}
        onCsv={(category) => setCsvFor(category)}
      />
      <VendorServiceCsvDialog
        open={csvFor !== null} initialCategory={csvFor || ''} onClose={() => setCsvFor(null)} vendorUserId={vendorUserId}
        serviceOptions={serviceOptions} onImported={() => { refetch(); refetchCats(); }}
        onOpenOffering={(id) => { refetchOfferings().then(({ data: d }) => { const o = (d?.myVendorOfferings || []).find(x => x.id === id); if (o) openEdit(o); }); }}
      />

      {!catLoading && !categories.length && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You don't have a services contract yet. First add the details of each service you offer (<strong>Add service details</strong>) and
          upload your documents in the checklist above. When a service's checklist is complete, request its contract: you sign it, the
          admin approves it, and your offerings go live.
        </Alert>
      )}

      {categories.map(cat => {
        const catOfferings = offerings.filter(o => o.category === cat.category);
        return (
          <Card key={cat.category} variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <StorefrontIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>{cat.displayName}</Typography>
                  <Chip
                    size="small"
                    color={cat.isAccepted ? 'success' : 'warning'}
                    label={cat.isSigned && !cat.isAccepted ? 'Awaiting your acceptance' : cat.isAccepted ? 'Contract accepted · live' : (STATUS_LABEL[cat.contractStatus] || cat.contractStatus)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {cat.contractRef ? `${cat.contractRef} · ` : ''}{cat.contractTitle}
                  </Typography>
                </Stack>
                <Button size="small" startIcon={<AddIcon />} disabled={!canAdd} onClick={() => openCreate(cat.category)}>Add {cat.displayName} offering</Button>
              </Stack>
              {!cat.isAccepted && (
                <Alert
                  severity="warning" variant="outlined" sx={{ mb: 2 }}
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="inherit" onClick={() => navigate(contractsPath)}>
                        {cat.isSigned ? 'Accept contract' : 'Open contract'}
                      </Button>
                      {cat.isVendorRequest && !cat.isSigned && (
                        <Button size="small" color="inherit" onClick={() => setConfirmWithdraw({
                          ...cat, others: categories.filter(c => c.contractId === cat.contractId && c.category !== cat.category).length,
                        })}>Withdraw {cat.displayName}</Button>
                      )}
                    </Stack>
                  }
                >
                  {pendingStep(cat)} You can prepare offerings now; they stay hidden from families and matchmakers until the contract is signed by you and the admin.
                </Alert>
              )}
              {!catOfferings.length && (
                <Typography variant="body2" color="text.secondary">No offerings yet for this service.</Typography>
              )}
              <Stack spacing={2} divider={<Divider flexItem />}>
                <Paged items={catOfferings}>{rows => rows.map(o => (
                  <Box key={o.id}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={700}>{o.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{o.businessName} · {o.city} · {formatPriceRange(o)}</Typography>
                        <Chip
                          size="small" sx={{ mt: 0.5 }}
                          icon={o.isPublished ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          color={o.isPublished ? 'success' : 'default'}
                          variant={o.isPublished ? 'filled' : 'outlined'}
                          label={o.visibilityNote}
                        />
                      </Box>
                      <Stack direction="row">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(o)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDelete(o)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                    <Typography variant="body2" sx={{ my: 1, whiteSpace: 'pre-line' }}>{o.description}</Typography>
                    <OfferingDetailList fields={fieldsForCategory(fieldDefs, o.category)} details={o.details} />
                    <OfferingPackagesView labels={packageLabelsFor(fieldDefs, o.category)} packages={o.packages} />
                  </Box>
                ))}</Paged>
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {draftServices.map(s => {
        const drafts = offerings.filter(o => o.category === s.category);
        if (!drafts.length) return null;
        return (
          <Card key={s.category} variant="outlined" sx={{ mb: 3, borderStyle: 'dashed' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={1}>
                <StorefrontIcon color="action" />
                <Typography variant="h6" fontWeight={700}>{s.displayName}</Typography>
                <Chip size="small" variant="outlined" label="Draft: not in a contract yet" />
                <Chip size="small" color={s.ready ? 'success' : 'warning'} label={s.ready ? 'Ready to request a contract' : 'Checklist not complete'} />
              </Stack>
              <Stack spacing={2} divider={<Divider flexItem />}>
                {drafts.map(o => (
                  <Box key={o.id}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={700}>{o.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{o.businessName} · {o.city} · {formatPriceRange(o)}</Typography>
                      </Box>
                      <Stack direction="row">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(o)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDelete(o)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                    <OfferingPackagesView labels={packageLabelsFor(fieldDefs, o.category)} packages={o.packages} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {/* Add / edit offering */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Offering' : 'Add Offering'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select size="small" label="Service *" value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value, details: {} }))}
              helperText="A service not in your contract yet is saved as a draft: request its contract once its checklist is complete" fullWidth>
              {serviceOptions.map(c => <MenuItem key={c.category} value={c.category}>{c.displayName}{c.contracted ? '' : ' (not in a contract yet)'}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Offering title *" value={form.title} onChange={set('title')} fullWidth placeholder="e.g. Traditional Tamil wedding feast" />
            <TextField size="small" label="Description *" value={form.description} onChange={set('description')} fullWidth multiline rows={3} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField size="small" label="Business name *" value={form.businessName} onChange={set('businessName')} fullWidth /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" label="City *" value={form.city} onChange={set('city')} fullWidth /></Grid>
              <Grid item xs={12}><TextField size="small" label="Service area" value={form.serviceArea} onChange={set('serviceArea')} fullWidth placeholder="e.g. Chennai, Kanchipuram, Chengalpattu" /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" type="number" label="Starting price (₹) *" value={form.priceFrom} onChange={set('priceFrom')} fullWidth inputProps={{ min: 0 }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" type="number" label="Maximum price (₹)" value={form.priceTo} onChange={set('priceTo')} fullWidth inputProps={{ min: 0 }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" label="Price unit" value={form.priceUnit} onChange={set('priceUnit')} fullWidth placeholder="per event / per plate / per day" /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" label="Contact name" value={form.contactName} onChange={set('contactName')} fullWidth /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" label="Contact phone *" value={form.contactPhone} onChange={set('contactPhone')} fullWidth /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" label="Contact email" value={form.contactEmail} onChange={set('contactEmail')} fullWidth /></Grid>
              {/* Shown to families before they book (Consumer Protection (E-Commerce) Rules, 2020) */}
              <Grid item xs={12}>
                <TextField size="small" label="Cancellation & refund terms *" value={form.cancellationPolicy} onChange={set('cancellationPolicy')} fullWidth multiline minRows={3}
                  placeholder="e.g. Advance of 30% to confirm. Cancel 60+ days before: full refund of advance. 30–60 days: 50% refunded. Under 30 days: advance not refunded. If we cancel, everything paid is refunded within 7 days."
                  helperText="Families see these before booking and must accept them. Keep them fair — they must follow the Consumer Protection Act, 2019." />
              </Grid>
            </Grid>
            {form.category && (
              <>
                <Divider textAlign="left">
                  <Typography variant="caption" fontWeight={700}>
                    {serviceOptions.find(c => c.category === form.category)?.displayName} details
                  </Typography>
                </Divider>
                <OfferingDetailInputs fields={detailFields} values={form.details} onChange={details => setForm(f => ({ ...f, details }))} />
                <Divider textAlign="left">
                  <Typography variant="caption" fontWeight={700}>{packageLabels.packages} &amp; {packageLabels.items.toLowerCase()}</Typography>
                </Divider>
                <OfferingPackagesEditor
                  category={form.category}
                  labels={packageLabels}
                  packages={form.packages}
                  defaultUnit={form.priceUnit && form.priceUnit !== 'per event' ? form.priceUnit : packageLabels.unit}
                  onChange={packages => setForm(f => ({ ...f, packages }))}
                />
              </>
            )}
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
              label="Active (offer this service)"
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* Also beside the button: the form is long and the message above may be out of view */}
          {error && <Typography variant="body2" color="error" sx={{ flex: 1, px: 1 }}>{error}</Typography>}
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating}>
            {editing ? 'Save changes' : 'Add offering'}
          </Button>
        </DialogActions>
      </Dialog>

      <ChooseServicesDialog open={chooseOpen} onClose={() => setChooseOpen(false)} onCreated={onContractCreated} vendorUserId={vendorUserId}
        onAddDetails={canAdd ? (category) => {
          setChooseOpen(false);
          const existing = (readiness?.services || []).find(s => s.category === category)?.offeringId;
          const o = existing && offerings.find(x => x.id === existing);
          if (o) openEdit(o); else openCreate(category);
        } : undefined} />

      {/* Withdraw one service from a contract request */}
      <Dialog open={Boolean(confirmWithdraw)} onClose={() => setConfirmWithdraw(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Withdraw {confirmWithdraw?.displayName}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmWithdraw?.others > 0
              ? `Only ${confirmWithdraw.displayName} is removed from ${confirmWithdraw.contractRef || 'this contract'}. `
                + `The other ${confirmWithdraw.others} service${confirmWithdraw.others === 1 ? '' : 's'} on it stay as they are.`
              : `${confirmWithdraw?.displayName} is the only service on ${confirmWithdraw?.contractRef || 'this contract'}, so the whole contract request is withdrawn.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmWithdraw(null)}>Cancel</Button>
          <Button color="warning" variant="contained"
            onClick={() => withdrawContract({ variables: { id: confirmWithdraw.contractId, category: confirmWithdraw.category } })}>
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete offering?</DialogTitle>
        <DialogContent>
          <Typography>"{confirmDelete?.title}" will be removed and no longer shown to families.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteOffering({ variables: { id: confirmDelete.id } })}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
